import React, { useCallback, useLayoutEffect, useRef } from "react";
import { Button, Space, Tooltip } from "antd";
import { RiEditFill, RiEditLine } from "react-icons/ri";
import CanvasTickMarks from "./CanvasTickMarks";
import CanvasImage from "./CanvasImage";
import GeoBackdrop from "../../../LiveDashboardModal/components/GeoBackdrop";
import FloorPaint from "../../../LiveDashboardModal/components/FloorPaint";
import ZoomControls from "../../../LiveDashboardModal/components/ZoomControls";
import LayerControls from "../../../LiveDashboardModal/components/LayerControls";
import CanvasRail from "../../../LiveDashboardModal/components/CanvasRail";
import BlankCanvasHint from "../../../LiveDashboardModal/components/BlankCanvasHint";
// 編輯模式 colour, shared by the canvas frame and the pressed toggle so the
// two always match (and with the 圖層 control's accent).
import { EDIT_COLOR } from "../../utils/constants";
import { canvasMode, editorCursor } from "../../utils/editorCursor";

/**
 * The plan editor's canvas box (#floor-plan-canvas) under the editor's
 * zoom/pan (useEditorMode). Layer order, bottom up: the map backdrop (地圖),
 * the plan image (平面圖), the floor paint (FloorPaint, as on the live
 * dashboard), the tick marks, then the host's children
 * (walkable area, stations, symbols, tripwires), then the control corners:
 * the 圖層 control (底圖 on a geo-referenced site, and the 顯示 rows) top-right, and in
 * 瀏覽 the zoom pill top-left.
 *
 * Without an `editor` (or at zoom 1 with the map off) it is the canvas box
 * the editor always had: same markup, same overflow:visible. It clips once
 * the view is zoomed or panned, or the map is on, because the projected
 * floor then extends past the box.
 */
const EditorViewport = ({
  canvasRef,
  editor,
  viewport,
  canvasSize,
  imageUrl,
  dimensions,
  onClick,
  onMouseMove,
  // 圖層's 顯示 rows (基站, 物件, 可行區域, 越線); 底圖 comes from editor.layers.
  layerToggles = [],
  children,
}) => {
  const attachCanvas = editor?.attachCanvas;
  const setRef = useCallback(
    (el) => {
      canvasRef.current = el;
      if (attachCanvas) attachCanvas(el);
    },
    [canvasRef, attachCanvas],
  );

  const view = editor?.view;
  const layers = editor?.layers;
  const mapVisible = !!layers?.mapVisible;
  const planVisible = layers?.planVisible ?? true;
  const planOpacity = layers?.planOpacity ?? 1;
  const gridVisible = layers?.gridVisible ?? true;
  const clip = !!view?.isTransformed || mapVisible;

  return (
    <div
      ref={setRef}
      id="floor-plan-canvas"
      style={{
        border: "1px solid #d9d9d9",
        borderRadius: 4,
        width: `${canvasSize.width}px`,
        height: `${canvasSize.height}px`,
        marginLeft: 40,
        marginBottom: 20,
        overflow: clip ? "hidden" : "visible",
        backgroundColor: "#ffffff",
        position: "relative",
        boxShadow: "inset 0 1px 3px rgba(0, 0, 0, 0.08)",
        // 編輯模式 marker: an outline takes no layout space, so the box and
        // every pixel <-> metre conversion stay exactly as in 瀏覽.
        ...(editor?.editMode ? { outline: `2px solid ${EDIT_COLOR}` } : {}),
        // 瀏覽 grabs (a drag pans), 編輯模式 is the arrow, a tool crosshairs.
        cursor: editorCursor(
          canvasMode(editor?.editing ?? true, !!onClick),
          "canvas",
          { dragging: view?.isDragging },
        ),
      }}
      onMouseDown={view?.handleMouseDown}
      onClickCapture={view?.handleClickCapture}
      onClick={onClick}
      onMouseMove={onMouseMove}
    >
      {mapVisible && viewport.isValid && (
        <GeoBackdrop
          geoRef={layers.geoRef}
          tileLayers={layers.tileLayers}
          layerId={layers.layerId}
          opacity={layers.mapOpacity}
          viewport={viewport}
          canvasSize={canvasSize}
          onUnavailable={layers.markUnavailable}
        />
      )}
      {/* The ground the plan image sits on and fades into (as on the live
          dashboard): the map or the surround, the no-image sheet, the grid. */}
      <FloorPaint
        layer="ground"
        canvasSize={canvasSize}
        viewport={viewport}
        zoom={view?.zoom}
        pan={view?.pan}
        hasImage={!!imageUrl}
        mapVisible={mapVisible}
        planVisible={planVisible}
        planOpacity={planOpacity}
        gridVisible={gridVisible}
      />
      {planVisible && (
        <CanvasImage
          imageUrl={imageUrl}
          dimensions={dimensions}
          canvasSize={canvasSize}
          view={view}
          opacity={planOpacity}
        />
      )}
      {/* The floor edge above the plan, as on the live dashboard. */}
      <FloorPaint
        layer="edge"
        canvasSize={canvasSize}
        viewport={viewport}
        zoom={view?.zoom}
        pan={view?.pan}
        hasImage={!!imageUrl}
        mapVisible={mapVisible}
        planVisible={planVisible}
        planOpacity={planOpacity}
        gridVisible={gridVisible}
      />
      <CanvasTickMarks canvasSize={canvasSize} viewport={viewport} />

      {children}

      <BlankCanvasHint layers={layers} />

      <CanvasRail>
        {/* LayerControls stops its own presses (and its portalled panel's)
            from reaching the canvas, where they would pan or place a point. */}
        {(layers?.available || layerToggles.length > 0) && (
          <LayerControls toggles={layerToggles} layers={layers} />
        )}
      </CanvasRail>
      {/* 瀏覽 only: in 編輯模式 the view holds still, so there is nothing for
          the zoom buttons to do, and the 可行區域 bar takes this corner. */}
      {editor?.zoomEnabled && !editor.editMode && (
        <CanvasRail side="left">
          <ZoomControls
            zoom={view.zoom}
            isTransformed={view.isTransformed}
            atMinZoom={view.atMinZoom}
            atMaxZoom={view.atMaxZoom}
            onZoomStep={view.zoomByStep}
            onReset={view.reset}
          />
        </CanvasRail>
      )}
    </div>
  );
};

// The app's global button CSS sets background and colour with !important on
// the button AND its inner spans (css/button.css, themes/antd-dark-override
// .css), which beats a plain inline style and draws antd's primary like
// default. Inline !important is the one thing that beats it.
const pressedStyles = [
  ["background-color", EDIT_COLOR],
  ["border-color", EDIT_COLOR],
  ["color", "#fff"],
];
const spanStyles = [
  ["background", "transparent"],
  ["color", "#fff"],
];
const applyImportant = (el, styles, on) =>
  styles.forEach(([prop, value]) =>
    on
      ? el.style.setProperty(prop, value, "important")
      : el.style.removeProperty(prop),
  );

/** The plane canvas card's title with the 編輯模式 toggle beside it. */
export const EditModeTitle = ({ editor }) => {
  const buttonRef = useRef(null);
  const on = editor.editMode;

  useLayoutEffect(() => {
    const el = buttonRef.current;
    if (!el) return;
    applyImportant(el, pressedStyles, on);
    Array.from(el.children).forEach((span) =>
      applyImportant(span, spanStyles, on),
    );
  }, [on]);

  return (
    <Space size="small">
      平面佈局畫布
      <Tooltip title="編輯模式下可拖曳、放置與繪製；瀏覽時可縮放與平移">
        <Button
          ref={buttonRef}
          type={on ? "primary" : "default"}
          size="small"
          aria-pressed={on}
          icon={on ? <RiEditFill /> : <RiEditLine />}
          onClick={editor.toggleEditMode}
        >
          編輯模式
        </Button>
      </Tooltip>
    </Space>
  );
};

export default EditorViewport;
